import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Menu,
  MenuItem,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  ListItemIcon,
  Grid2 as Grid,
  Divider,
} from "@mui/material";
import { Container } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import { Link, Link as RouterLink, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faBars,
  faLock,
  faArchive,
  faBook,
  faChartBar,
  faClipboardList,
  faEnvelope,
  faInfoCircle,
  faUserPlus,
  faUsers,
  faSignOutAlt,
  faChalkboardTeacher,
  faDownload,
  faUserGraduate,
  faPhone,
  faComment,
  faClose,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../contexts/AuthContext";
import { LoginDialog } from "./LoginDialog";
import { useLoginDialog } from "../hooks/useLoginDialog";
import useNotification from "../hooks/useNotification";
import ChangePasswordDialog from "./Admin/ChangePasswordDialog";
import { AlertTriangle } from "lucide-react";

const MobileHeader: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const { showNotification } = useNotification();
  const { user, logout, isLoggedIn } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const loginDialog = useLoginDialog();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] =
    useState(false);

  const navItems = [
    { label: "Main", path: "/", icon: faBook },
    { label: "Courses", path: "/courses", icon: faBook },
    { label: "Register for Course", path: "/register", icon: faUserPlus },
    {
      label: "Online Partner Training",
      path: "/online-course",
      icon: faChalkboardTeacher,
    },
    { label: "Resources", path: "/resources", icon: faInfoCircle },
    { label: "Contact Us", path: "/contact", icon: faEnvelope },
  ];

  const adminNavItems = [
    {
      label: "Course Management",
      path: "/admin/courses",
      icon: faClipboardList,
    },
    {
      label: "Archived Courses",
      path: "/admin/archived-courses",
      icon: faArchive,
    },
    {
      label: "Participant Management",
      path: "/admin/participants",
      icon: faUserGraduate,
    },
    { label: "User Management", path: "/admin/users", icon: faUsers },
    { label: "Reports", path: "/admin/reports", icon: faChartBar },
  ];

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleChangePassword = () => {
    handleMenuClose();
    setChangePasswordDialogOpen(true);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
  };

  const handleDownloadInvitationTemplate = () => {
    const templateUrl = process.env.REACT_APP_INVITATION_TEMPLATE_URL;
    if (templateUrl) {
      window.open(templateUrl, "_blank");
    } else {
      console.error("Invitation template URL is not defined");
      showNotification("Invitation template URL is not defined", "error");
    }
    handleMenuClose();
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: "end" }}>
      <FontAwesomeIcon fontSize="28px" color="#002436" style={{ paddingRight: "5%", paddingTop: "5%" }} icon={faClose} onClick={handleMenuClose} /> 
      <List>
        {navItems.map((item) => (
          <ListItem key={item.label} component={RouterLink} to={item.path}>
            <ListItemText style={{ marginLeft: "5%", color: "#002436", textTransform: "uppercase" }} primary={item.label} />
            <Divider style={{ borderColor: "#002436", height: "25px", width: "90%", marginRight: "5%", marginTop: "10px", position: "absolute"  }} orientation="horizontal" />
          </ListItem>
        ))}
        {isLoggedIn &&
          adminNavItems.map((item) => (
            <ListItem key={item.label} component={RouterLink} to={item.path}>
              <ListItemText style={{ marginLeft: "5%", color: "#002436", textTransform: "uppercase" }} primary={item.label} />
            <Divider style={{ borderColor: "#002436", height: "25px", width: "90%", marginRight: "5%", marginTop: "10px", position: "absolute"  }} orientation="horizontal" />
          </ListItem>
          ))}
      </List>
    </Box>
  );

  return (
    <AppBar position="static" color="default">
      <div style={{backgroundColor: 'white', display: 'flex', justifyContent: 'center'}}>
        <Grid container spacing={2}>
          <Grid size={12} style={{transform: "translateY(5px)", display: "flex", justifyContent: "center", alignItems: "center"}}>
            <AlertTriangle fill="black" color="white" style={{ display: "inline", marginRight: "5px" }} className="w-5 h-5" />{" "}
              <p style={{ display: "inline", fontSize: "13px", margin: "0px" }}>
                  Report Emergencies, Crimes, or Suspicious Activity:
              </p>
          </Grid>
          <Grid size={6} style={{transform: "translate(20px, -5px)", fontSize: "13px", display: "flex", justifyContent: "center", alignItems: "center"}}>
            <FontAwesomeIcon icon={faPhone} className="mr-2" />
            <span className="font-bold">(800) 331-0008</span>
          </Grid>
          <Grid size={6} style={{transform: "translate(-20px, -5px)", fontSize: "13px", display: "flex", justifyContent: "center", alignItems: "center"}}>
            <FontAwesomeIcon icon={faComment} className="mr-2" />
            <span className="font-bold">APD11 (27311)</span>
          </Grid>
        </Grid>
      </div>
      <Container maxWidth="lg" style={{backgroundColor: "#051C2C", paddingTop: "4px"}}>
        <Toolbar disableGutters>
          <Link to="/" style={{ textDecoration: "none", flexGrow: 1 }}>
            <Box
              component="img"
              sx={{ height: 55 }}
              alt="Operation RailSafe"
              src="/images/railsafe-logo.png"
            />
          </Link>
          {isMobile ? (
            <>
              <IconButton
                sx={{ p: 0 }}
                color="inherit"
                style={{color:"white"}}
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
              >
                <FontAwesomeIcon size="1x" icon={faBars} />
              </IconButton>
              <Drawer
                PaperProps={{
                  sx: { width: "100%" },
                }}
                anchor="right"
                open={drawerOpen}
                onClose={handleDrawerToggle}
              >
                {drawer}
              </Drawer>
            </>
          ) : (
            <>
              {navItems.map((item) => (
                <Button
                  key={item.label}
                  component={RouterLink}
                  to={item.path}
                  sx={{
                    ...(location.pathname === item.path && {
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      mx: 1,
                      height: "64px",
                      borderRadius: "0px",
                    }),
                  }}
                  startIcon={<FontAwesomeIcon icon={item.icon} />}
                >
                  {item.label}
                </Button>
              ))}
            </>
          )}
        </Toolbar>
      </Container>
      <LoginDialog
        isOpen={loginDialog.isOpen}
        setIsOpen={loginDialog.setIsOpen}
      />
      <ChangePasswordDialog
        open={changePasswordDialogOpen}
        onClose={() => setChangePasswordDialogOpen(false)}
      />
    </AppBar>
  );
};

export default MobileHeader;
